/*
   Data-fetch layer for the review queue.

   `threadsQuery` — server-paginated + searchable + sortable page of QueueRows.
     Backend does the heavy lifting; the FE hook only maps the wire to UI rows.
     Client-side filters (assignee/submitter dropdowns) still apply *within the
     current page* — full-set filtering waits on backend #22 (denormalize
     assignee_name/client_name on ThreadOut so the row already carries them).

   `queueTotalsQuery` — a small unfiltered fetch used only to render the four
     status stat cards. Capped at page_size=100. Real fix: backend #14 (GET
     /reviews/stats). Until then this is deliberately best-effort.
*/

import {
  keepPreviousData,
  queryOptions,
  useQuery,
  type UseQueryResult,
} from "@tanstack/react-query"

import { readAuthSession } from "@/lib/auth-token"
import { ApiError } from "@/lib/errors"
import { http } from "@/lib/http"
import { fromPageWire, toQueryParams, type Page, type PageWire } from "@/lib/pagination"
import { qk } from "@/lib/query-keys"
import { canListClients, toRole } from "@/lib/roles"
import type { TableQueryParams } from "@/lib/table"

import {
  toQueueRow,
  type QueueEnrichment,
  type QueueRow,
  type QueueStatus,
  type ThreadOutWire,
} from "./queue-data"

/** GET /api/v1/reviews returns a list of ReviewSummary — the fields we need for enrichment. */
interface ReviewSummaryWire {
  id: number
  assignee_id: number | null
  client_id: number | null
  created_at: string
  completed_at: string | null
}

interface ClientOutWire {
  id: number
  name: string
}

interface PersonOutWire {
  id: number
  full_name: string | null
}

interface ReviewPeopleOutWire {
  assignable: PersonOutWire[]
}

/**
 * Best-effort enrichment for the visible page only. A 403 from /clients (non-admin)
 * or a failed /people call leaves the fields as `—`, which is preferable to failing
 * the whole queue over a missing name.
 */
async function fetchEnrichment(
  threads: readonly ThreadOutWire[],
  signal: AbortSignal,
): Promise<QueueEnrichment> {
  const reviewMeta = new Map<
    number,
    { assigneeId: number | null; clientId: number | null; updatedAt: string }
  >()
  const userNames = new Map<number, string>()
  const clientNames = new Map<number, string>()

  const reviewsPromise = Promise.all([
    http
      .get<ReviewSummaryWire[]>("/api/v1/reviews", { params: { archived: false }, signal })
      .catch(() => [] as ReviewSummaryWire[]),
    http
      .get<ReviewSummaryWire[]>("/api/v1/reviews", { params: { archived: true }, signal })
      .catch(() => [] as ReviewSummaryWire[]),
  ]).then(([a, b]) => [...a, ...b])

  // The clients-list endpoint is admin-only; skip the enrichment call for anyone else so we
  // don't burn a round-trip on a guaranteed 403. Row-scoped users see their engagement's name
  // via the /reviews enrichment below.
  const role = toRole(readAuthSession()?.user.role)
  const clientsPromise: Promise<ClientOutWire[]> = canListClients(role)
    ? http
        .get<PageWire<ClientOutWire>>("/api/v1/clients", {
          params: { include_inactive: true, page_size: 100 },
          signal,
        })
        .then((r) => r.items)
        .catch((err: unknown) => {
          if (err instanceof ApiError && err.status === 403) return []
          return []
        })
    : Promise.resolve([])

  const anyReviewId = threads[0]?.current_review_id
  const peoplePromise =
    anyReviewId != null
      ? http
          .get<ReviewPeopleOutWire>(`/api/v1/reviews/${String(anyReviewId)}/people`, { signal })
          .catch(() => null)
      : Promise.resolve(null)

  const [reviews, clients, people] = await Promise.all([
    reviewsPromise,
    clientsPromise,
    peoplePromise,
  ])

  for (const r of reviews) {
    reviewMeta.set(r.id, {
      assigneeId: r.assignee_id,
      clientId: r.client_id,
      updatedAt: r.completed_at ?? r.created_at,
    })
  }
  for (const c of clients) clientNames.set(c.id, c.name)
  if (people) {
    for (const u of people.assignable) {
      if (u.full_name) userNames.set(u.id, u.full_name)
    }
  }
  return { reviewMeta, userNames, clientNames }
}

export interface ThreadsQueryParams {
  page: number
  pageSize: number
  search: string
  sort: { field: string; dir: "asc" | "desc" } | null
  includeArchived: boolean
}

async function fetchThreadsPage(
  params: ThreadsQueryParams,
  signal: AbortSignal,
): Promise<Page<QueueRow>> {
  const wire = await http.get<PageWire<ThreadOutWire>>("/api/v1/threads", {
    params: toQueryParams(
      {
        page: params.page,
        pageSize: params.pageSize,
        sort: params.sort,
        filters: {},
        search: params.search,
      } satisfies TableQueryParams,
      { include_archived: params.includeArchived },
    ),
    signal,
  })
  const enrichment = await fetchEnrichment(wire.items, signal)
  return fromPageWire(wire, (t) => toQueueRow(t, enrichment))
}

export const threadsQuery = (params: ThreadsQueryParams) =>
  queryOptions({
    queryKey: qk.threads.list(params),
    queryFn: ({ signal }) => fetchThreadsPage(params, signal),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  })

export function useThreadsQuery(
  params: ThreadsQueryParams,
): UseQueryResult<Page<QueueRow>> {
  return useQuery(threadsQuery(params))
}

/**
 * Cheap unfiltered fetch used only by the four status stat cards.
 *
 * Fetches at most 100 threads and counts them client-side. **This means the stat cards
 * under-report on any queue with more than 100 threads** — a known gap. The right fix is
 * backend task #14 (a dedicated ``GET /reviews/stats`` that returns bucketed counts
 * without shipping row data). Until then the cards are accurate at seed scale (~50
 * threads) and *approximate* beyond, and `hasMore` on the wire is the FE's signal to
 * annotate them accordingly.
 */
export interface QueueTotals {
  counts: Record<QueueStatus, number>
  total: number
  countsAreApproximate: boolean
}

async function fetchQueueTotals(signal: AbortSignal): Promise<QueueTotals> {
  const pageSize = 100
  const wire = await http.get<PageWire<ThreadOutWire>>("/api/v1/threads", {
    params: { include_archived: true, page_size: pageSize },
    signal,
  })
  const rows = wire.items.map((t) => toQueueRow(t))
  const counts: Record<QueueStatus, number> = {
    queued: 0,
    in_review: 0,
    needs_sme: 0,
    cleared: 0,
  }
  for (const r of rows) counts[r.status]++
  return { counts, total: wire.total, countsAreApproximate: wire.total > pageSize }
}

export const queueTotalsQuery = () =>
  queryOptions({
    queryKey: [...qk.threads.all, "totals"] as const,
    queryFn: ({ signal }) => fetchQueueTotals(signal),
    staleTime: 30_000,
  })

export function useQueueTotalsQuery(): UseQueryResult<QueueTotals> {
  return useQuery(queueTotalsQuery())
}
